import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { UserRegistration, InviteRegistration } from '../models/UserRegistration';
import { Tfa } from '../interfaces/tfa.interface';
import { environment } from '../environments/environment';
import { NetworkHelper } from '../helpers/network.helper';
import { SignIn, User } from '../interfaces/user.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  readonly apiUrl = `${environment.poolApi}/user`;
  readonly mmsApiUrl = `${environment.mmsApi}/user`;

  constructor(private networkHelper: NetworkHelper) {}

  signup(user: UserRegistration): Observable<User> {
    return this.networkHelper.post<User>(this.apiUrl, user);
  }

  updateUserInfo(firstName: string, lastName: string, name: string): Observable<User> {
    return this.networkHelper.put<User>(this.apiUrl, {
      firstName,
      lastName,
      name,
    });
  }

  confirmAccount(code: string): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/confirm/${code}`, {});
  }

  resendConfirmation(email: string): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/resendconfirmation`, {
      email,
    });
  }

  signIn(email: string, password: string, remember?: boolean): Observable<SignIn> {
    return this.networkHelper.post<SignIn>(`${this.apiUrl}/login`, {
      email,
      password,
      remember: remember,
    });
  }

  logout(): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/logout`, {});
  }

  passwordRecover(email: string): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/password/recover`, {
      email,
    });
  }

  checkForgotPassword(code: string): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/check/forgot/${code}`, {});
  }

  passwordConfirm(code: string, password: string): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/password/confirm`, {
      password,
      code,
    });
  }

  changePassword(currentPassword: string, newPassword: string): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/password/reset`, {
      currentPassword,
      newPassword,
    });
  }

  getTfaSecretKey(): Observable<Tfa> {
    return this.networkHelper.post<Tfa>(`${this.apiUrl}/tfa/enable`, {});
  }

  enableTfa(tfaCode: string): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/tfa/verify`, {
      tfaCode,
    });
  }

  disableTFA(tfaCode: string): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/tfa/disable`, {
      tfaCode,
    });
  }

  checkInvitationCode(code: string): Observable<User> {
    return this.networkHelper.get<User>(`${this.mmsApiUrl}/check-code/${code}`, {});
  }

  signupAfterInvitate(code: string, userRegistration: InviteRegistration): Observable<User> {
    return this.networkHelper.post<User>(`${this.apiUrl}/confirmInvitation`, { code, ...userRegistration });
  }
}
